import { Task, TaskStatus, TaskPriority, TaskLabel, TaskFilter } from '@/types/task-management';
import { v4 as uuidv4 } from 'uuid';
import { mockTaskLabels } from '@/lib/mock/task-management';
import { supabase } from '@/lib/supabase';

// Converter funções para mapear entre os dados do DB e o frontend
function mapDbTaskToTask(dbTask: any): Task {
  return {
    id: dbTask.id,
    title: dbTask.title,
    description: dbTask.description,
    status: dbTask.status,
    priority: dbTask.priority,
    due_date: dbTask.due_date,
    created_at: dbTask.created_at,
    updated_at: dbTask.updated_at,
    created_by: dbTask.created_by,
    assigned_to: null, // Sempre null por enquanto
    labels: [],
    is_personal: true, // Por padrão, assumimos que é uma tarefa pessoal
    added_to_my_day: dbTask.added_to_my_day,
    playbook_id: dbTask.playbook_id,
    playbook_name: dbTask.playbook_name,
    client_id: dbTask.client_id,
    subtasks: [],
    comments: []
  };
}

// Interface para o resultado da consulta de tarefas
interface TaskResult {
  tasks: Task[];
  count: number;
}

// TaskService para realizar operações CRUD
export const TaskService = {
  // Obter todas as tarefas com filtros opcionais
  async getTasks(filter?: TaskFilter): Promise<TaskResult> {
    try {
      let query = supabase.from('tasks')
        .select('*', { count: 'exact' })
        .is('deleted_at', null);

      // Aplicar filtros se existirem
      if (filter) {
        if (filter.status && filter.status.length > 0) {
          query = query.in('status', filter.status);
        }
        
        if (filter.priority && filter.priority.length > 0) {
          query = query.in('priority', filter.priority);
        }
        
        if (filter.playbook_id) {
          query = query.eq('playbook_id', filter.playbook_id);
        }

        if (filter.assigned_to) {
          query = query.eq('assignee_id', filter.assigned_to);
        }

        // Se o filtro incluir tarefas pessoais
        if (filter.is_personal === true) {
          query = query.eq('added_to_my_day', true);
        }

        if (filter.due_date_start) {
          query = query.gte('due_date', filter.due_date_start);
        }

        if (filter.due_date_end) {
          query = query.lte('due_date', filter.due_date_end);
        }

        if (filter.search) {
          query = query.or(`title.ilike.%${filter.search}%,description.ilike.%${filter.search}%`);
        }
      }

      const { data, count, error } = await query;

      if (error) {
        throw error;
      }

      // Mapear dados do banco para o formato do frontend
      const tasks = await Promise.all(
        (data || []).map(async (dbTask) => {
          const task = mapDbTaskToTask(dbTask);
          
          // Carregar labels
          const { data: labelsData } = await supabase
            .from('task_label_relations')
            .select('label_id, task_labels!inner(*)')
            .eq('task_id', task.id)
            .is('task_labels.deleted_at', null);
          
          if (labelsData) {
            // Extrair apenas os IDs dos labels
            task.labels = labelsData.map(item => {
              // @ts-ignore - Necessário porque o tipo de retorno do Supabase não é tipado corretamente
              return item.task_labels.id as string;
            });
          }
          
          // Carregar subtasks/checklist
          const { data: subtasksData } = await supabase
            .from('task_checklist_items')
            .select('*')
            .eq('task_id', task.id)
            .is('deleted_at', null)
            .order('order_index', { ascending: true });
          
          if (subtasksData) {
            // Mapear para o formato esperado pelo frontend
            task.subtasks = subtasksData.map(item => ({
              id: item.id,
              title: item.title,
              completed: item.is_completed
            }));
          }
          
          // Carregar comentários (apenas IDs por enquanto)
          const { data: commentsData } = await supabase
            .from('task_comments')
            .select('id')
            .eq('task_id', task.id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false });
          
          if (commentsData) {
            task.comments = commentsData.map(item => item.id);
          }
          
          return task;
        })
      );

      return { tasks, count: count || 0 };
    } catch (error) {
      console.error('Erro ao buscar tarefas:', error);
      throw error;
    }
  },

  // Obter uma tarefa por ID
  async getTaskById(id: string): Promise<Task | null> {
    try {
      const { data: dbTask, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single();

      if (error || !dbTask) {
        return null;
      }

      // Criar objeto de tarefa base
      const task = mapDbTaskToTask(dbTask);
      
      // Carregar dados relacionados (labels, subtasks, comentários)
      // Labels
      const { data: labelsData } = await supabase
        .from('task_label_relations')
        .select('label_id, task_labels!inner(*)')
        .eq('task_id', id)
        .is('task_labels.deleted_at', null);
      
      if (labelsData) {
        // Extrair apenas os IDs dos labels
        task.labels = labelsData.map(item => {
          // @ts-ignore - Necessário porque o tipo de retorno do Supabase não é tipado corretamente
          return item.task_labels.id as string;
        });
      }
      
      // Subtasks
      const { data: subtasksData } = await supabase
        .from('task_checklist_items')
        .select('*')
        .eq('task_id', id)
        .is('deleted_at', null)
        .order('order_index', { ascending: true });
      
      if (subtasksData) {
        // Mapear para o formato esperado pelo frontend
        task.subtasks = subtasksData.map(item => ({
          id: item.id,
          title: item.title,
          completed: item.is_completed
        }));
      }
      
      // Comentários (apenas IDs)
      const { data: commentsData } = await supabase
        .from('task_comments')
        .select('id')
        .eq('task_id', id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      
      if (commentsData) {
        task.comments = commentsData.map(item => item.id);
      }

      return task;
    } catch (error) {
      console.error('Erro ao buscar tarefa por ID:', error);
      throw error;
    }
  },

  // Criar uma nova tarefa
  async createTask(taskData: Partial<Task>, userId: string): Promise<Task> {
    try {
      // Preparar dados para inserção
      const now = new Date().toISOString();
      const taskId = uuidv4();
      
      const taskToInsert = {
        id: taskId,
        title: taskData.title || 'Nova tarefa',
        description: taskData.description || '',
        status: taskData.status || 'todo',
        priority: taskData.priority || 'medium',
        due_date: taskData.due_date,
        added_to_my_day: taskData.added_to_my_day || false,
        assignee_id: null,
        parent_task_id: null,
        playbook_id: taskData.playbook_id,
        playbook_name: taskData.playbook_name,
        client_id: taskData.client_id,
        created_by: userId,
        updated_by: userId,
        created_at: now,
        updated_at: now
      };

      // Inserir tarefa no banco
      const { data, error } = await supabase
        .from('tasks')
        .insert(taskToInsert)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Inserir labels se existirem
      if (taskData.labels && taskData.labels.length > 0) {
        const labelRelations = taskData.labels.map(labelId => ({
          task_id: taskId,
          label_id: labelId,
          created_at: now
        }));

        const { error: labelsError } = await supabase
          .from('task_label_relations')
          .insert(labelRelations);

        if (labelsError) {
          console.error('Erro ao associar labels:', labelsError);
        }
      }

      // Inserir subtasks se existirem
      if (taskData.subtasks && taskData.subtasks.length > 0) {
        const subtasksToInsert = taskData.subtasks.map((subtask, index) => ({
          id: uuidv4(),
          task_id: taskId,
          title: subtask.title,
          is_completed: subtask.completed || false,
          completed_at: subtask.completed ? now : null,
          order_index: index,
          created_by: userId,
          updated_by: userId,
          created_at: now,
          updated_at: now
        }));

        const { error: subtasksError } = await supabase
          .from('task_checklist_items')
          .insert(subtasksToInsert);

        if (subtasksError) {
          console.error('Erro ao criar subtasks:', subtasksError);
        }
      }

      // Buscar a tarefa completa com relacionamentos
      return this.getTaskById(taskId) as Promise<Task>;
    } catch (error) {
      console.error('Erro ao criar tarefa:', error);
      throw error;
    }
  },

  // Atualizar uma tarefa existente
  async updateTask(taskId: string, updates: Partial<Task>, userId: string): Promise<Task> {
    try {
      // Verificar se a tarefa existe
      const existingTask = await this.getTaskById(taskId);
      if (!existingTask) {
        throw new Error('Tarefa não encontrada');
      }

      const now = new Date().toISOString();
      
      // Preparar dados para atualização
      const taskToUpdate = {
        ...(updates.title !== undefined && { title: updates.title }),
        ...(updates.description !== undefined && { description: updates.description }),
        ...(updates.status !== undefined && { status: updates.status }),
        ...(updates.priority !== undefined && { priority: updates.priority }),
        ...(updates.due_date !== undefined && { due_date: updates.due_date }),
        ...(updates.assigned_to !== undefined && { assignee_id: null }),
        ...(updates.added_to_my_day !== undefined && { added_to_my_day: updates.added_to_my_day }),
        ...(updates.playbook_id !== undefined && { playbook_id: updates.playbook_id }),
        ...(updates.playbook_name !== undefined && { playbook_name: updates.playbook_name }),
        ...(updates.client_id !== undefined && { client_id: updates.client_id }),
        updated_by: userId,
        updated_at: now,
        // Se o status mudou para completo, atualizar o completed_at
        ...(updates.status === 'completed' && { completed_at: now }),
        // Se o status mudou de completo para outro, limpar o completed_at
        ...(updates.status && updates.status !== 'completed' && { completed_at: null })
      };
      
      // Atualizar tarefa no banco
      const { error } = await supabase
        .from('tasks')
        .update(taskToUpdate)
        .eq('id', taskId);

      if (error) {
        throw error;
      }

      // Atualizar labels se fornecidos
      if (updates.labels) {
        // Remover relações existentes
        await supabase
          .from('task_label_relations')
          .delete()
          .eq('task_id', taskId);

        // Adicionar novas relações
        if (updates.labels.length > 0) {
          const labelRelations = updates.labels.map(labelId => ({
            task_id: taskId,
            label_id: labelId,
            created_at: now
          }));

          await supabase
            .from('task_label_relations')
            .insert(labelRelations);
        }
      }

      // Atualizar subtasks se fornecidas
      if (updates.subtasks) {
        // Obter subtasks existentes
        const { data: existingSubtasks } = await supabase
          .from('task_checklist_items')
          .select('id')
          .eq('task_id', taskId)
          .is('deleted_at', null);

        const existingIds = new Set((existingSubtasks || []).map(item => item.id));
        const updatedIds = new Set(updates.subtasks.filter(s => s.id).map(s => s.id));

        // Identificar subtasks para excluir (soft delete)
        const idsToDelete = [...existingIds].filter(id => !updatedIds.has(id));
        if (idsToDelete.length > 0) {
          await supabase
            .from('task_checklist_items')
            .update({ deleted_at: now, updated_by: userId, updated_at: now })
            .in('id', idsToDelete);
        }

        // Atualizar/inserir subtasks
        for (let i = 0; i < updates.subtasks.length; i++) {
          const subtask = updates.subtasks[i];
          
          if (subtask.id && existingIds.has(subtask.id)) {
            // Atualizar subtask existente
            await supabase
              .from('task_checklist_items')
              .update({
                title: subtask.title,
                is_completed: subtask.completed,
                completed_at: subtask.completed ? now : null,
                order_index: i,
                updated_by: userId,
                updated_at: now
              })
              .eq('id', subtask.id);
          } else {
            // Inserir nova subtask
            await supabase
              .from('task_checklist_items')
              .insert({
                id: subtask.id || uuidv4(),
                task_id: taskId,
                title: subtask.title,
                is_completed: subtask.completed || false,
                completed_at: subtask.completed ? now : null,
                order_index: i,
                created_by: userId,
                updated_by: userId,
                created_at: now,
                updated_at: now
              });
          }
        }
      }

      // Buscar a tarefa atualizada com todos os relacionamentos
      return this.getTaskById(taskId) as Promise<Task>;
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
      throw error;
    }
  },

  // Excluir uma tarefa (soft delete)
  async deleteTask(taskId: string, userId: string): Promise<void> {
    try {
      const now = new Date().toISOString();
      
      await supabase
        .from('tasks')
        .update({
          deleted_at: now,
          updated_at: now,
          updated_by: userId
        })
        .eq('id', taskId);
    } catch (error) {
      console.error('Erro ao excluir tarefa:', error);
      throw error;
    }
  },

  // Adicionar ou remover tarefa do "Meu Dia"
  async toggleMyDay(taskId: string, added: boolean, userId: string): Promise<void> {
    try {
      const now = new Date().toISOString();
      
      await supabase
        .from('tasks')
        .update({
          added_to_my_day: added,
          updated_at: now,
          updated_by: userId
        })
        .eq('id', taskId);
    } catch (error) {
      console.error('Erro ao atualizar Meu Dia:', error);
      throw error;
    }
  },

  // Alterar o status de uma tarefa
  async changeTaskStatus(taskId: string, status: TaskStatus, userId: string): Promise<void> {
    try {
      const now = new Date().toISOString();
      
      await supabase
        .from('tasks')
        .update({
          status,
          updated_at: now,
          updated_by: userId,
          // Se o status mudou para completo, atualizar o completed_at
          ...(status === 'completed' && { completed_at: now }),
          // Se o status mudou de completo para outro, limpar o completed_at
          ...(status !== 'completed' && { completed_at: null })
        })
        .eq('id', taskId);
    } catch (error) {
      console.error('Erro ao alterar status da tarefa:', error);
      throw error;
    }
  },

  // Obter todos os labels
  async getTaskLabels(): Promise<TaskLabel[]> {
    try {
      const { data, error } = await supabase
        .from('task_labels')
        .select('*')
        .is('deleted_at', null)
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      // Se não houver dados ou a lista estiver vazia, retornar labels mock para desenvolvimento
      if (!data || data.length === 0) {
        console.log("Nenhum label encontrado no banco, usando mock data");
        return mockTaskLabels;
      }

      return data.map(label => ({
        id: label.id,
        name: label.name,
        color: label.color
      }));
    } catch (error) {
      console.error('Erro ao buscar labels:', error);
      // Em caso de erro, retornar labels mock para não quebrar a UI
      console.log("Erro ao buscar labels, usando mock data");
      return mockTaskLabels;
    }
  },

  // Criar um novo label
  async createTaskLabel(label: Omit<TaskLabel, 'id'>, userId: string): Promise<TaskLabel> {
    try {
      const now = new Date().toISOString();
      const labelId = uuidv4();

      const { data, error } = await supabase
        .from('task_labels')
        .insert({
          id: labelId,
          name: label.name,
          color: label.color,
          created_by: userId,
          updated_by: userId,
          created_at: now,
          updated_at: now
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      return {
        id: data.id,
        name: data.name,
        color: data.color
      };
    } catch (error) {
      console.error('Erro ao criar label:', error);
      throw error;
    }
  },

  // Adicionar comentário a uma tarefa
  async addTaskComment(taskId: string, content: string, userId: string): Promise<{ id: string }> {
    try {
      const now = new Date().toISOString();
      const commentId = uuidv4();
      
      const { data, error } = await supabase
        .from('task_comments')
        .insert({
          id: commentId,
          task_id: taskId,
          user_id: null, // Usando null para o user_id temporariamente
          content,
          created_by: userId,
          updated_by: userId,
          created_at: now,
          updated_at: now
        })
        .select('id')
        .single();
      
      if (error) {
        throw error;
      }
      
      return { id: commentId };
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      throw error;
    }
  },

  // Adicionar um anexo à tarefa
  async addTaskAttachment(taskId: string, file: File, userId: string): Promise<any> {
    try {
      // Upload do arquivo para o storage do Supabase
      const filePath = `task-attachments/${taskId}/${uuidv4()}-${file.name}`;
      
      const { data: uploadData, error: uploadError } = await supabase
        .storage
        .from('task-files')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Obter URL pública do arquivo
      const { data: urlData } = await supabase
        .storage
        .from('task-files')
        .getPublicUrl(filePath);

      const fileUrl = urlData.publicUrl;

      // Registrar o anexo no banco de dados
      const attachment = {
        id: uuidv4(),
        task_id: taskId,
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        file_url: fileUrl,
        created_by: userId,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('task_attachments')
        .insert(attachment)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro ao adicionar anexo:', error);
      throw error;
    }
  }
};

export default TaskService; 